import { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, HeartHandshake, Leaf, Send, Truck, CalendarDays } from 'lucide-react';
import HalloweenSchoolGallery from '../components/HalloweenSchoolGallery';
import Seo from '../components/Seo';
import { CONTACT } from '../data/siteData';
import './HalloweenCommunity.css';

const EMPTY={
  kind:'vehicle',name:'',phone:'',email:'',address:'',
  preferredPickup:'',vehicleYear:'',vehicleMake:'',vehicleModel:'',
  vehiclePlate:'',details:'',accessNotes:'',ownershipConfirmed:false,website:''
};
const kinds=[
  {key:'vehicle',symbol:'🚙',name:'Unwanted Vehicle',detail:'Scrap cars, trucks or vehicles to evaluate for pickup'},
  {key:'metal',symbol:'♻️',name:'Scrap Metal',detail:'Metal, equipment and recyclable materials'},
  {key:'other',symbol:'🎃',name:'Other Support',detail:'Materials or help for the Halloween school experiences'}
];

function HalloweenDonationForm(){
  const [form,setForm]=useState({...EMPTY});
  const [state,setState]=useState('idle');
  const [error,setError]=useState('');
  const [received,setReceived]=useState(null);
  const [photos,setPhotos]=useState([]);
  const [uploadedCount,setUploadedCount]=useState(0);
  const [photoError,setPhotoError]=useState('');
  const [photoStatus,setPhotoStatus]=useState('');
  const [uploading,setUploading]=useState(false);
  const photoInput=useRef(null);
  const requestId=useRef(null);
  const busy=useRef(false);
  const change=(field,value)=>setForm(current=>({...current,[field]:value}));
  const input=(field)=>({value:form[field],onChange:event=>change(field,event.target.value)});
  function choosePhotos(event) {
    const selected=Array.from(event.target.files||[]);
    const kinds=new Set(['image/jpeg','image/png','image/webp']);
    if(selected.length>6||selected.some(file=>!kinds.has(file.type)||file.size===0||file.size>6*1024*1024)){
      setPhotoError('Select up to six JPG, PNG or WebP photographs, each no larger than 6 MB.');
      setPhotos([]);event.target.value='';return;
    }
    setPhotoError('');setPhotos(selected);setUploadedCount(0);
  }
  async function uploadDonationPhotos(url,start=0){
    if(!url||!/^https:\/\/sdrjrbstmfvltxlreunm\.supabase\.co\/functions\/v1\/job-photo-upload\?token=[a-f0-9]{64}$/.test(url)){
      setPhotoError('Your donation call is saved, but photo uploads are currently unavailable. Dispatch can request the pictures separately.');
      return;
    }
    setUploading(true);setPhotoError('');
    try{
      for(let index=start;index<photos.length;index++){
        setPhotoStatus('Uploading photograph '+(index+1)+' of '+photos.length+'…');
        const body=new FormData();
        body.append('file',photos[index],photos[index].name);
        const controller=new AbortController();
        const timeout=setTimeout(()=>controller.abort(),45000);
        let result,data;
        try{
          result=await fetch(url,{method:'POST',body,signal:controller.signal,referrerPolicy:'no-referrer'});
          data=await result.json().catch(()=>null);
        }finally{clearTimeout(timeout);}
        if(!result.ok||data?.ok!==true)
          throw new Error(data?.error||'The server could not confirm that this photograph was saved.');
        setUploadedCount(index+1);
      }
      setPhotoStatus('All '+photos.length+' photograph'+(photos.length===1?'':'s')+' saved securely with your dispatch call.');
    }catch(uploadFailure){
      setPhotoStatus('');
      setPhotoError((uploadFailure.name==='AbortError'?'A photo upload timed out. ':'' )+
        (uploadFailure.message||'Not all photographs could be uploaded.')+
        ' Your donation request is saved. You can retry the remaining photographs.');
    }finally{setUploading(false);}
  }
  async function submit(event){
    event.preventDefault();
    if(busy.current||received)return;
    busy.current=true;setState('sending');setError('');
    try{
      requestId.current ||= crypto.randomUUID();
      const controller=new AbortController();
      const timer=setTimeout(()=>controller.abort(),26000);
      let res,data;
      try{
        res=await fetch('/api/halloween-donations',{
          method:'POST',headers:{'Content-Type':'application/json'},
          credentials:'same-origin',
          body:JSON.stringify({...form,requestId:requestId.current}),
          signal:controller.signal
        });
        data=await res.json().catch(()=>null);
      }finally{clearTimeout(timer);}
      if(!res.ok||data?.ok!==true||!/^[A-Z0-9-]{5,35}$/.test(data.reference||''))
        throw new Error(data?.error||'We could not confirm delivery. Please retry or contact dispatch.');
      setReceived(data);setState('success');
      if(photos.length)await uploadDonationPhotos(data.photoUploadUrl,0);
      if(!data.duplicate){
        try{window.rokaAnalytics?.track?.('lead_submit',{ctaId:'halloween_donation',ctaLabel:'School Halloween donation'});}catch{/* Analytics must never interrupt a confirmed donation request. */}
      }
    }catch(e){
      setError(e.name==='AbortError'?'Delivery could not be confirmed yet. Retry this form with the same details, or call dispatch.':e.message||'Donation request could not be confirmed.');
      setState('error');
    }finally{busy.current=false;}
  }
  return <section id="donate" className="jhDonate" aria-labelledby="jh-form-heading">
    <div className="jhFormIntro">
      <span className="jhEyebrow">🎃 Donation pickup request</span>
      <h2 id="jh-form-heading">Make a donation. Make their Halloween.</h2>
      <p>Tell us what you'd like to contribute. Your request goes directly to Jonny's Towing dispatch for review and pickup coordination.</p>
      <div className="jhDispatchNote"><Truck size={20}/><span>Submitting does not confirm acceptance or pickup. We'll contact you to review availability, materials, vehicle ownership and safe collection.</span></div>
    </div>
    <div className="jhFormPanel">
      {received?<div className="jhSuccess" role="status" aria-live="polite">
        <span className="jhPumpkin">🎃</span>
        <h3>Thank you for supporting the school Halloween events!</h3>
        <p>Your donation offer has been sent directly to dispatch.</p>
        <strong>Dispatch reference: {received.reference}</strong>
        <p>We'll contact you to confirm whether we can accept the donation and arrange collection.</p>
        {received.duplicate&&<p>Your previously submitted request was found — no duplicate call was created.</p>}
        {photos.length>0&&<div className="jhPhotoResult" aria-live="polite">
          <strong>Collection photographs: {uploadedCount} / {photos.length} received</strong>
          {uploading&&<p>{photoStatus||'Uploading securely…'}</p>}
          {!uploading&&photoStatus&&<p>{photoStatus}</p>}
          {photoError&&<p className="jhPhotoError" role="alert">{photoError}</p>}
          {!uploading&&uploadedCount<photos.length&&received.photoUploadUrl&&
            <button className="jhButton jhButton--outline" type="button" onClick={()=>void uploadDonationPhotos(received.photoUploadUrl,uploadedCount)}>Retry remaining photos</button>}
        </div>}
        <button type="button" className="jhButton jhButton--outline" disabled={uploading} onClick={()=>{setForm({...EMPTY});setReceived(null);setPhotos([]);setUploadedCount(0);setPhotoError('');setPhotoStatus('');requestId.current=null;setState('idle');setError('');if(photoInput.current)photoInput.current.value='';}}>Submit another donation</button>
      </div>:<form onSubmit={submit} aria-busy={state==='sending'}>
        <h3>Tell us about your donation</h3>
        <div className="jhKinds" role="group" aria-label="Donation type">
          {kinds.map(item=><button key={item.key} type="button"
            className={'jhKind '+(form.kind===item.key?'isSelected':'')}
            aria-pressed={form.kind===item.key} onClick={()=>change('kind',item.key)}>
            <span aria-hidden="true">{item.symbol}</span><strong>{item.name}</strong><small>{item.detail}</small>
          </button>)}
        </div>
        <fieldset disabled={state==='sending'} className="jhFields">
          <div className="jhFormGrid">
            <label><span>Your name *</span><input {...input('name')} required minLength={2} maxLength={160} autoComplete="name" placeholder="Full name"/></label>
            <label><span>Best phone number *</span><input {...input('phone')} type="tel" required minLength={7} maxLength={40} autoComplete="tel" placeholder="604-555-0123"/></label>
            <label className="jhFull"><span>Email (optional)</span><input {...input('email')} type="email" maxLength={200} autoComplete="email" placeholder="you@example.com"/></label>
            <label className="jhFull"><span>Where is the donation located? *</span><input {...input('address')} required minLength={5} maxLength={500} autoComplete="street-address" placeholder="Full pickup address, city, BC"/></label>
            {form.kind==='vehicle'&&<>
              <label><span>Vehicle year</span><input {...input('vehicleYear')} maxLength={10} placeholder="2008"/></label>
              <label><span>Make</span><input {...input('vehicleMake')} maxLength={100} placeholder="Chevrolet"/></label>
              <label><span>Model</span><input {...input('vehicleModel')} maxLength={100} placeholder="Silverado"/></label>
              <label><span>Plate (if available)</span><input {...input('vehiclePlate')} maxLength={30} placeholder="Optional"/></label>
            </>}
            <label className="jhFull"><span>What would you like to donate? *</span><textarea {...input('details')} required minLength={5} maxLength={1800} rows={4}
              placeholder={form.kind==='vehicle'?'Tell us whether the vehicle runs, rolls, has keys or needs special equipment.':form.kind==='metal'?'Describe the metal, approximate quantity, dimensions and how it is stored.':'Tell us what you would like to contribute to the Halloween school events.'}/></label>
            <label><span>Preferred collection time</span><input {...input('preferredPickup')} maxLength={120} placeholder="e.g. weekday afternoons"/></label>
            <label><span>Collection access</span><input {...input('accessNotes')} maxLength={600} placeholder="Driveway, alley, gate or other details"/></label>
            <label className="jhFull jhPhotoPicker"><span>Photos of your donation (optional)</span>
              <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={choosePhotos}/>
              <small>Add up to six photos showing the vehicle, scrap metal, and safe access for our truck. JPG, PNG or WebP, 6 MB per photo. Photos remain private in TowOps and are not shown in the public school gallery.</small>
              {photos.length>0&&<strong>{photos.length} photo{photos.length===1?'':'s'} selected</strong>}
              {photoError&&<span className="jhPhotoError" role="alert">{photoError}</span>}
            </label>
            <label className="jhFull jhConsent"><input type="checkbox" required checked={form.ownershipConfirmed} onChange={e=>change('ownershipConfirmed',e.target.checked)}/><span>I own these items or have authorization to offer them, and understand Jonny's Towing will confirm acceptance and pickup details before collecting them. *</span></label>
            <label className="jhTrap" aria-hidden="true"><span>Leave blank</span><input {...input('website')} tabIndex={-1} autoComplete="off"/></label>
          </div>
          <button className="jhButton" type="submit"><Send size={18}/>{state==='sending'?'Sending to dispatch…':'🎃 Send Donation to Dispatch'}<ArrowRight size={18}/></button>
        </fieldset>
        <p className="jhPrivacy">Your information is used to review and coordinate your donation offer. <Link to="/privacy-policy">Privacy policy</Link>. We do not promise a donation tax receipt.</p>
        {error&&<div className="jhError" role="alert">{error} <a href={CONTACT.phoneHref}>Contact dispatch</a>.</div>}
      </form>}
    </div>
  </section>;
}

export default function HalloweenCommunity(){
  const location=useLocation();
  useEffect(()=>{
    if(location.hash==='#donate'){
      const timer=setTimeout(()=>document.getElementById('donate')?.scrollIntoView({behavior:'smooth'}),50);
      return()=>clearTimeout(timer);
    }
  },[location.hash]);
  return <main className="jhCommunity">
    <Seo title="Donate Scrap Cars & Metal for School Halloween | Jonny's Towing Richmond BC"
      description="Donate an unwanted vehicle or eligible scrap metal to support Jonny's Towing school Halloween and pumpkin events in Richmond, BC. Send a pickup request directly to dispatch."
      image="/images/jonnys-halloween-giving-og.jpg" canonicalPath="/community/halloween"/>
    <header className="jhHero">
      <div className="jhHeroImage" role="img" aria-label="Original Halloween artwork: Jonny's Towing flatbed decorated with pumpkins, lanterns, autumn trees and glowing lights.">
        <picture><source srcSet="/images/jonnys-halloween-giving.avif" type="image/avif"/><img src="/images/jonnys-halloween-giving-og.jpg" alt="Jonny's Towing Halloween donation initiative: original cinematic pumpkin-decorated flatbed artwork" loading="eager"/></picture>
      </div>
      <div className="jhHeroCopy">
        <span className="jhEyebrow">Jonny's Towing & Recovery · Richmond, BC</span>
        <h1>Your unwanted vehicle.<br/><em>Their unforgettable Halloween.</em></h1>
        <p>Eligible unwanted vehicles, scrap metal and materials can help support the extraordinary Halloween and pumpkin experiences we create for local schools.</p>
        <div className="jhHeroActions"><a className="jhButton" href="#donate">🎃 Donate a Vehicle or Scrap Metal <ArrowRight size={18}/></a><a className="jhButton jhButton--outline" href="#school-impact">See the School Magic</a></div>
        <small>Artwork is an original AI-created scene; school event photos below show our real community work.</small>
      </div>
    </header>
    <div className="jhFeatures" aria-label="How your donations help">
      <div><Truck size={32}/><h2>Vehicles & metal</h2><p>Offer a scrap vehicle, equipment or suitable recyclable materials for pickup review.</p></div>
      <div><HeartHandshake size={32}/><h2>For the schools</h2><p>Help us put time, resources and creativity toward meaningful seasonal experiences.</p></div>
      <div><Leaf size={32}/><h2>A second purpose</h2><p>Turn unwanted materials into an opportunity to contribute to our community.</p></div>
    </div>
    <section className="jhImpact" id="school-impact">
      <div className="jhImpactIntro">
        <span className="jhEyebrow">Real school events · Real community involvement</span>
        <h2>See the magic we create.</h2>
        <p>Our school pumpkin events combine themed flatbed deliveries, decorated schoolyards and hands-on collaboration with volunteers. The gallery will grow with photographs from our own events, not imagery borrowed from other attractions.</p>
        <Link className="jhTextLink" to="/school-events">Explore our school pumpkin events <ArrowRight size={18}/></Link>
      </div>
      
    </section>
    <HalloweenSchoolGallery/>
    <HalloweenDonationForm/>
    <footer className="jhEnd"><CalendarDays size={26}/><p>More than towing. A tradition of making Halloween special for our community.</p><Link to="/in-the-community">Explore our community work <ArrowRight size={16}/></Link></footer>
  </main>;
}